import Box from '@mui/material/Box';
import List from '@mui/material/List';
import { FormProvider, useForm } from 'react-hook-form';
import { IRecruiterTeamNameAutoCompleteOption } from 'wisestep-api';
import { WsDrawer } from 'wisestep-base';

import {
  transformClientRecruitersToEmailArray,
  transformIClientDTOsToStringArray,
} from '../../../features/jobs/helper';
import { ClientAutocomplete } from '../../ClientAutocomplete';
import { ClientRecruiterAutocomplete } from '../../ClientRecruiterAutocomplete';
import {
  CreatedBwDatesFilter,
  CurrentEmployersFilter,
  JobTitleFilter,
  JobTypeFilter,
  LanguagesFilter,
  NoticePeriod,
  PreviousEmployersFilter,
  RecruitersFilter,
  SalaryRangeFilter,
  SourceFilter,
  StageStatusFilter,
  TagsFilter,
  TeamFilter,
  TotalExperienceFilter,
  WsAccordion,
} from '../components';
import AccountManagerUserFilter from '../components/AccountManagerFilter';
import AdvanceFilterFooterBar from '../components/AdvanceFilterFooterBar';
import {
  candidateStatus,
  workAuthorization,
  contactPreferences,
  jobPreferences,
} from '../components/AdvanceFilterHelper';
import CheckboxListFilter from '../components/CheckboxListFilter';
import MappingsAdvanceFilterFormProps from '../models/MappingAdvanceFiltersFormProps';
import MappingAdvanceFilterProps from '../models/MappingAdvanceFiltersProps';

const WsMappingAdvanceFilters = ({
  open,
  filterClose,
  onApplyFilters,
  defaultValues,
}: MappingAdvanceFilterProps) => {
  const methods = useForm<MappingsAdvanceFilterFormProps>({ defaultValues: defaultValues });
  const { handleSubmit, reset } = methods;
  const onFormSubmit = (data: MappingsAdvanceFilterFormProps) => {
    onApplyFilters({
      ...data,
      clients: transformIClientDTOsToStringArray(data.clientList ?? []),
      clientRecruiters: transformClientRecruitersToEmailArray(data.clientRecruiterList ?? []),
    });
    filterClose();
  };

  return (
    <FormProvider {...methods}>
      <WsDrawer
        title="Advanced Filters"
        open={open}
        onClose={() => filterClose()}
        size="medium"
        headerSize="medium"
      >
        <Box
          component="form"
          role="presentation"
          onSubmit={handleSubmit(onFormSubmit)}
          onReset={() => {
            reset();
            onFormSubmit({});
          }}
        >
          <Box pb={7}>
            <List>
              <JobTitleFilter name="jobTitles" />
              <CreatedBwDatesFilter
                name="createBWDates.fromDateName"
                name1="createBWDates.toDateName"
              />
              <CheckboxListFilter
                name="workAuthorization"
                options={workAuthorization}
                title="Work Authorization"
              />
              <SourceFilter name="source" defaultValue={defaultValues.source} />
              <CheckboxListFilter
                name="commPreferences"
                options={contactPreferences}
                title="Communication Preference"
              />
              <CurrentEmployersFilter
                name="currentEmployers"
                defaultValue={defaultValues.currentEmployers}
              />
              <PreviousEmployersFilter
                name="previousEmployers"
                defaultValue={defaultValues.previousEmployers}
              />
              <CheckboxListFilter
                name="contactAvailability"
                options={contactPreferences}
                title="Contact Availability"
              />
              <SalaryRangeFilter />
              <NoticePeriod name="noticePeriod" />
              <LanguagesFilter name="languages" />
              <JobTypeFilter name="jobType" />
              <CheckboxListFilter
                name="jobPreference"
                options={jobPreferences}
                title="Job Preference"
              />
              <WsAccordion title="Client">
                <ClientAutocomplete name="clientList" multiple={true} />
              </WsAccordion>
              <WsAccordion title="Client Recruiter">
                <ClientRecruiterAutocomplete
                  name="clientRecruiterList"
                  multiple={true}
                  clients={methods.watch('clientList') ?? []}
                />
              </WsAccordion>
              <WsAccordion title="Account Managers">
                <AccountManagerUserFilter
                  name="accountManagers"
                  multiple={true}
                  isBorderStyle={true}
                />
              </WsAccordion>
              <RecruitersFilter
                name="recruiters"
                defaultValue={defaultValues.recruiters}
                title="Recruiters"
              />
              <TeamFilter
                name="teams"
                title="Teams"
                defaultValue={defaultValues.teams?.map((obj) => obj.name as string) ?? []}
                multiple
                defaultObjects={
                  methods.getValues('teams') as IRecruiterTeamNameAutoCompleteOption[]
                }
              />
              <TotalExperienceFilter />
              <StageStatusFilter name="stageStatus" />
              <CheckboxListFilter name="candidateStatus" options={candidateStatus} title="Status" />
              <TagsFilter name="tags" defaultValue={defaultValues.tags} />
            </List>
          </Box>
          <AdvanceFilterFooterBar />
        </Box>
      </WsDrawer>
    </FormProvider>
  );
};
export default WsMappingAdvanceFilters;
